'use client';

import React, { useState, useCallback } from 'react';
import { Check, ChevronDown, MoveRight, PauseCircle, X } from 'lucide-react';
import { applicationApi, type Application } from '../services/application.api';
import type { PipelineStage } from '@/modules/job/services/job.api';
import { useFullPagerStore } from '@/modules/shared/store/useFullPagerStore';
import { useAuth } from '@/auth/AuthProvider';

interface Props {
    application: Application;
    pipelineStages: PipelineStage[];
    onRefresh: () => void;
}

export default function ApplicationActionBar({ application, pipelineStages, onRefresh }: Props) {
    const { hasPermission } = useAuth();
    const canReject = hasPermission('application.reject');
    const [loading, setLoading] = useState(false);
    const [showMoveMenu, setShowMoveMenu] = useState(false);

    const sortedStages = [...pipelineStages].sort((a, b) => a.order - b.order);
    const currentIdx = sortedStages.findIndex((s) => s._id === application.currentStageId);
    const nextStage = currentIdx >= 0 && currentIdx < sortedStages.length - 1 ? sortedStages[currentIdx + 1] : null;

    const handleMove = useCallback(async (targetStageId: string) => {
        setLoading(true);
        try {
            await applicationApi.moveApplication(application._id, targetStageId);
            onRefresh();
        } catch (err) {
            console.error('Move failed:', err);
        } finally {
            setLoading(false);
            setShowMoveMenu(false);
        }
    }, [application._id, onRefresh]);

    const handleReject = useCallback(async () => {
        setLoading(true);
        try {
            await applicationApi.rejectApplication(application._id);
            onRefresh();
        } catch (err) {
            console.error('Reject failed:', err);
        } finally {
            setLoading(false);
        }
    }, [application._id, onRefresh]);

    const { navigationContext, navigateTo } = useFullPagerStore();

    const handleHold = useCallback(() => {
        if (navigationContext && navigationContext.currentIndex < navigationContext.total - 1) {
            navigateTo(navigationContext.currentIndex + 1);
        }
    }, [navigationContext, navigateTo]);

    if (application.overallStatus !== 'active') {
        const tone = application.overallStatus === 'hired'
            ? { bg: 'var(--bg-success)', fg: 'var(--fg-success)', label: 'Hired' }
            : application.overallStatus === 'rejected'
                ? { bg: 'var(--bg-danger)', fg: 'var(--fg-danger)', label: 'Rejected' }
                : { bg: 'var(--surface-tertiary)', fg: 'var(--text-tertiary)', label: application.overallStatus };
        return (
            <span
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', borderRadius: 9999,
                    fontSize: 13, fontWeight: 600,
                    background: tone.bg, color: tone.fg,
                }}
            >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone.fg }} />
                {tone.label}
            </span>
        );
    }

    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, position: 'relative', fontFamily: 'var(--font-sans)' }}>
            {nextStage && (
                <button
                    onClick={() => handleMove(nextStage._id)}
                    disabled={loading}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 7,
                        height: 36, padding: '0 16px',
                        borderRadius: 10,
                        background: 'var(--accent)',
                        color: 'white',
                        border: '1px solid var(--accent-strong)',
                        boxShadow: '0 1px 0 0 rgba(0,0,0,0.06), inset 0 1px 0 0 rgba(255,255,255,0.18)',
                        fontSize: 13, fontWeight: 600,
                        fontFamily: 'inherit',
                        cursor: loading ? 'wait' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        transition: 'transform 80ms, box-shadow 80ms',
                    }}
                    onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0.5px)'; }}
                    onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = ''; }}
                >
                    <Check size={14} strokeWidth={2.5} />
                    <span>Accept</span>
                    <span style={{ opacity: 0.7, fontWeight: 400 }}>·</span>
                    <span style={{ fontWeight: 500 }}>{nextStage.name}</span>
                </button>
            )}

            {canReject && (
                <button
                    onClick={handleReject}
                    disabled={loading}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        height: 36, padding: '0 14px',
                        borderRadius: 10,
                        background: 'var(--bg-danger)',
                        color: 'var(--fg-danger)',
                        border: '1px solid rgba(207,34,46,0.18)',
                        fontSize: 13, fontWeight: 600,
                        fontFamily: 'inherit',
                        cursor: loading ? 'wait' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        transition: 'background 120ms',
                    }}
                >
                    <X size={14} strokeWidth={2.25} />
                    Reject
                </button>
            )}

            <button
                onClick={handleHold}
                disabled={loading}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    height: 36, padding: '0 14px',
                    borderRadius: 10,
                    background: 'var(--surface-primary)',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border-default)',
                    fontSize: 13, fontWeight: 500,
                    fontFamily: 'inherit',
                    cursor: loading ? 'wait' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    transition: 'background 120ms',
                }}
            >
                <PauseCircle size={14} strokeWidth={1.75} />
                Hold
            </button>

            {/* Divider */}
            <span style={{ width: 1, height: 22, background: 'var(--border-muted)', margin: '0 4px' }} />

            <div style={{ position: 'relative' }}>
                <button
                    onClick={() => setShowMoveMenu(!showMoveMenu)}
                    disabled={loading}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        height: 36, padding: '0 14px',
                        borderRadius: 10,
                        background: 'var(--surface-secondary)',
                        color: 'var(--text-secondary)',
                        border: '1px solid var(--border-default)',
                        fontSize: 13, fontWeight: 500,
                        fontFamily: 'inherit',
                        cursor: loading ? 'wait' : 'pointer',
                        opacity: loading ? 0.7 : 1,
                        transition: 'background 120ms',
                    }}
                >
                    <MoveRight size={14} strokeWidth={1.75} />
                    Move to
                    <ChevronDown
                        size={12}
                        strokeWidth={1.75}
                        style={{
                            color: 'var(--text-tertiary)',
                            transform: showMoveMenu ? 'rotate(180deg)' : 'none',
                            transition: 'transform 160ms',
                        }}
                    />
                </button>
                {showMoveMenu && (
                    <>
                        <div
                            onClick={() => setShowMoveMenu(false)}
                            style={{ position: 'fixed', inset: 0, zIndex: 10 }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                bottom: 'calc(100% + 8px)', left: 0,
                                width: 220,
                                background: 'var(--surface-primary)',
                                border: '1px solid var(--border-default)',
                                borderRadius: 12,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.05)',
                                padding: 6,
                                zIndex: 20,
                                fontFamily: 'var(--font-sans)',
                                maxHeight: 280, overflowY: 'auto',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 10.5, fontWeight: 600,
                                    color: 'var(--text-tertiary)',
                                    textTransform: 'uppercase', letterSpacing: '0.08em',
                                    padding: '6px 10px 4px',
                                }}
                            >
                                Move to stage
                            </div>
                            {sortedStages
                                .filter((s) => s._id !== application.currentStageId)
                                .map((s) => (
                                    <button
                                        key={s._id}
                                        onClick={() => handleMove(s._id)}
                                        style={{
                                            width: '100%',
                                            textAlign: 'left',
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '8px 10px',
                                            borderRadius: 8,
                                            background: 'transparent',
                                            border: 'none',
                                            color: 'var(--text-primary)',
                                            fontSize: 13, fontFamily: 'inherit',
                                            cursor: 'pointer',
                                            transition: 'background 100ms',
                                        }}
                                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-secondary)'; }}
                                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                    >
                                        <span
                                            style={{
                                                width: 6, height: 6, borderRadius: '50%',
                                                background: 'var(--text-quaternary)',
                                                flexShrink: 0,
                                            }}
                                        />
                                        {s.name}
                                    </button>
                                ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
